import React, { useState, useEffect } from 'react';
import { getValidationStatus, pauseValidation, resumeValidation } from '../services/api';

/**
 * ValidationProgressPanel - Shows validation progress with current step (e.g. "Fetching LinkedIn profiles"),
 * Pause / Resume / Cancel options, and Refresh status to see latest step.
 * initialRunTotal: when starting validation with selected records, pass the count so we show "0/3" not "0/19" immediately.
 */
const ValidationProgressPanel = ({ taskId, isProcessing, onRestart, initialRunTotal }) => {
  const [status, setStatus] = useState({
    processing: 0,
    total: initialRunTotal ?? 0,
    qualified: 0,
    rejected: 0,
    error: 0,
    current_step: 'Processing...',
    paused: false
  });
  const [actionLoading, setActionLoading] = useState(null);

  const fetchStatus = async () => {
    if (!taskId) return;
    try {
      const response = await getValidationStatus(taskId);
      if (response.success && response.data) {
        const d = response.data;
        const apiTotal = (d.total_run !== undefined && d.total_run !== null) ? Number(d.total_run) : (d.total || 0);
        const total = (initialRunTotal != null && initialRunTotal > 0) ? initialRunTotal : apiTotal;
        const isRunScoped = (initialRunTotal != null && initialRunTotal > 0) || (d.total_run != null && d.total_run > 0);

        let qualified, rejected, error;
        if (isRunScoped) {
          // Run-scoped progress: use only run counts so we never show "9 completed of 2 total"
          qualified = d.qualified_run != null ? d.qualified_run : 0;
          rejected = d.rejected_run != null ? d.rejected_run : 0;
          error = d.error_run != null ? d.error_run : 0;
        } else {
          qualified = d.qualified_run != null ? d.qualified_run : (d.qualified || 0);
          rejected = d.rejected_run != null ? d.rejected_run : (d.rejected || 0);
          error = d.error_run != null ? d.error_run : (d.error || 0);
          if (d.total_run != null && d.qualified_run == null && d.rejected_run == null && d.error_run == null) {
            const taskCompleted = (d.qualified || 0) + (d.rejected || 0) + (d.error || 0);
            const capped = Math.min(taskCompleted, d.total_run);
            qualified = capped;
            rejected = 0;
            error = 0;
          }
        }

        const processing = d.processing_run != null ? d.processing_run : (d.processing || 0);
        setStatus({
          processing,
          total,
          qualified,
          rejected,
          error,
          current_step: d.current_step || 'Processing...',
          paused: !!d.paused
        });
      }
    } catch (error) {
      console.error('Error fetching validation status:', error);
    }
  };

  useEffect(() => {
    if (!taskId || !isProcessing) return;
    fetchStatus();
  }, [taskId, isProcessing, initialRunTotal]);

  const handlePause = async () => {
    if (!taskId || actionLoading) return;
    setActionLoading('pause');
    try {
      await pauseValidation(taskId);
      await fetchStatus();
    } catch (e) {
      console.error('Error pausing:', e);
    }
    setActionLoading(null);
  };

  const handleResume = async () => {
    if (!taskId || actionLoading) return;
    setActionLoading('resume');
    try {
      await resumeValidation(taskId);
      await fetchStatus();
    } catch (e) {
      console.error('Error resuming:', e);
    }
    setActionLoading(null);
  };

  const handleRefreshStatus = async () => {
    if (!taskId || actionLoading) return;
    setActionLoading('refresh');
    await fetchStatus();
    setActionLoading(null);
  };

  const completed = status.qualified + status.rejected + status.error;
  const displayTotal = (initialRunTotal != null && initialRunTotal > 0) ? initialRunTotal : status.total;
  // When run-scoped: if completed > displayTotal it's stale task-level data (first fetch before initialRunTotal was set) — show 0 until next fetch
  const displayCompleted = (displayTotal > 0 && completed > displayTotal) ? 0 : completed;
  if (!isProcessing) return null;

  return (
    <div className="mb-6 bg-gradient-to-r from-emerald-900/60 to-blue-900/60 rounded-2xl shadow-xl p-6 border-2 border-emerald-500/50">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Validation Progress</h3>
            <p className="text-sm text-emerald-200 mt-0.5">
              {status.total === 0
                ? 'Loading status...'
                : status.paused
                  ? 'Paused'
                  : status.processing > 0
                    ? `${status.processing} total record(s) in processing`
                    : 'Finishing up...'}
            </p>
            <p className="text-sm text-white font-semibold mt-0.5">
              {displayTotal === 0 ? '—' : displayCompleted} completed of {displayTotal === 0 ? '—' : displayTotal} total
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleRefreshStatus}
            disabled={!!actionLoading}
            className="px-4 py-2.5 bg-slate-700/80 hover:bg-slate-600 text-white rounded-xl font-medium text-sm transition-all border border-slate-500/50 flex items-center gap-2 disabled:opacity-50"
            title="Refresh to see latest step (e.g. Fetching LinkedIn profiles)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh status
          </button>
          {status.paused ? (
            <button
              type="button"
              onClick={handleResume}
              disabled={!!actionLoading}
              className="px-4 py-2.5 bg-emerald-500/80 hover:bg-emerald-500 text-white rounded-xl font-semibold text-sm transition-all border border-emerald-400/50 flex items-center gap-2 disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Resume
            </button>
          ) : (
            <button
              type="button"
              onClick={handlePause}
              disabled={!!actionLoading}
              className="px-4 py-2.5 bg-amber-500/80 hover:bg-amber-500 text-white rounded-xl font-semibold text-sm transition-all border border-amber-400/50 flex items-center gap-2 disabled:opacity-50"
              title="Pause validation"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Pause
            </button>
          )}
          {onRestart && (
            <button
              type="button"
              onClick={onRestart}
              disabled={!!actionLoading}
              className="px-4 py-2.5 bg-red-500/80 hover:bg-red-500 text-white rounded-xl font-semibold text-sm transition-all border border-red-400/50 flex items-center gap-2 disabled:opacity-50"
              title="Stop validation and start again"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel / Stop
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValidationProgressPanel;
